import { useCallback, useMemo } from 'react';
import { parseErrorMessage } from '../../../../core/api/api.context';
import {
  useMutationApproveCarrier,
  useMutationEditCarrier,
  useMutationEditCarrierByID,
  useQueryGetCarrier,
  useQueryGetCarrierHierarchy,
  useQueryListEffectiveServicing
} from '../../../carriers/carriers.queries';
import {
  useMutationEditCarrierOrganisation,
  useMutationSetRegistrationTarget
} from '../../../carriers/carrier-organisations.queries';
import { useQueryGetGroup } from '../../../groups/groups.queries';
import { needsReview } from '../../../carriers/logic/carrier-review';
import { CarrierGroupPanel } from '../../components/carrier-group-panel/carrier-group-panel.component';
import { EnrichedSettingsNodeDto } from '../../reducer/settings-tree-reducer.types';
import { EntityForm } from '../form.types';
import { CarrierGroupForm, CarrierGroupFormData, carrierGroupVariant } from './form';
import {
  CarrierGroupChanges,
  isApproval,
  splitOrganisationChanges,
  toCarrierGroupFormOutput,
  toEditCarrierDto
} from './form-output';
import { makeCarrierGroupFormSchema } from './form-schema';
import { makeCarrierGroupFormUISchema } from './ui-schema';

const TITLES = {
  organisation: 'Carriers Group Settings',
  unit: 'Carrier Group Settings'
} as const;

/**
 * Both kinds of group node. A unit reads and writes through the carriers endpoints. The
 * organisation is a container those refuse, so it reads via the groups controller and the
 * hierarchy, writes via the organisation endpoints, and keeps its policies on its target unit.
 */
export function useEditCarrierGroupForm(
  node: EnrichedSettingsNodeDto | undefined,
  readOnly: boolean = false
): EntityForm<CarrierGroupForm> | null {
  const groupID = node?.entityId;
  const variant = carrierGroupVariant(node?.subType);
  const isOrganisation = variant === 'organisation';

  // Unit: everything comes from the carrier itself.
  const carrier = useQueryGetCarrier(isOrganisation ? undefined : groupID);

  // Organisation: group for name and status, hierarchy for units and target, target unit for
  // policies. The hierarchy is positional, so ask from the managing project.
  const group = useQueryGetGroup(isOrganisation ? groupID : undefined);
  const hierarchyProjectID = isOrganisation ? group.data?.managedBy?.id ?? node?.request.rootId : undefined;
  const hierarchy = useQueryGetCarrierHierarchy(hierarchyProjectID, { enabled: isOrganisation });
  const registrationTargetID = hierarchy.data?.organisation?.registrationTargetGroupID;
  const target = useQueryGetCarrier(isOrganisation ? registrationTargetID : undefined);

  const servicing = useQueryListEffectiveServicing(groupID);

  const editCarrier = useMutationEditCarrier(groupID ?? '');
  const approveCarrier = useMutationApproveCarrier();
  const editTarget = useMutationEditCarrierByID();
  const editOrganisation = useMutationEditCarrierOrganisation();
  const setRegistrationTarget = useMutationSetRegistrationTarget();

  const defaultValues = useMemo(
    () =>
      isOrganisation
        ? CarrierGroupFormData.FromOrganisation(group.data, hierarchy.data, target.data)
        : CarrierGroupFormData.FromUnit(carrier.data),
    [isOrganisation, group.data, hierarchy.data, target.data, carrier.data]
  );

  const initialFormOutput = useMemo(() => toCarrierGroupFormOutput(defaultValues), [defaultValues]);

  const units = useMemo(
    () => (hierarchy.data?.units ?? []).map((unit) => ({ id: unit.id, name: unit.name })),
    [hierarchy.data]
  );

  const canApprove = !!carrier.data && needsReview(carrier.data);

  const formSchema = useMemo(
    () =>
      makeCarrierGroupFormSchema({
        variant,
        units,
        hasRegistrationTarget: !!registrationTargetID,
        canApprove,
        readOnly
      }),
    [variant, units, registrationTargetID, canApprove, readOnly]
  );

  const uiSchema = useMemo(() => makeCarrierGroupFormUISchema(variant), [variant]);

  const onSubmit = useCallback(
    async (changes: CarrierGroupChanges) => {
      if (!groupID) {
        return;
      }

      if (!isOrganisation) {
        const dto = toEditCarrierDto(changes);
        if (Object.keys(dto).length > 0) {
          await editCarrier.mutateAsync(dto);
        }
        // Last, so a refused edit leaves the flag in place.
        if (isApproval(changes)) {
          await approveCarrier.mutateAsync(groupID);
        }
        return;
      }

      const { organisation, registrationTarget, targetPolicies } = splitOrganisationChanges(changes);
      if (organisation) {
        await editOrganisation.mutateAsync({ groupID, dto: organisation });
      }
      if (registrationTarget) {
        await setRegistrationTarget.mutateAsync({ groupID, registrationTargetGroupID: registrationTarget });
      }
      // Policies follow the target, so a target changed in the same save receives them.
      const policiesTargetID = registrationTarget ?? registrationTargetID;
      if (targetPolicies && policiesTargetID) {
        await editTarget.mutateAsync({ carrierID: policiesTargetID, dto: targetPolicies });
      }
    },
    [
      groupID,
      isOrganisation,
      editCarrier,
      approveCarrier,
      editOrganisation,
      setRegistrationTarget,
      editTarget,
      registrationTargetID
    ]
  );

  const renderInsideCard = useCallback(
    () => (
      <CarrierGroupPanel
        isOrganisation={isOrganisation}
        servicing={servicing.data ?? []}
        isLoading={servicing.isLoading}
        error={
          servicing.isError ? parseErrorMessage(servicing.error?.message) || 'Could not load networks.' : undefined
        }
      />
    ),
    [isOrganisation, servicing.data, servicing.isLoading, servicing.isError, servicing.error]
  );

  if (!node || !groupID) {
    return null;
  }

  const isLoading = isOrganisation
    ? group.isLoading || hierarchy.isLoading || (!!registrationTargetID && target.isLoading)
    : carrier.isLoading;

  return {
    isLoading,
    title: TITLES[variant],
    subtitle: groupID,
    allowSubtitleCopy: true,
    defaultValues,
    initialFormOutput,
    formSchema,
    uiSchema,
    getOutput: toCarrierGroupFormOutput,
    onSubmit,
    renderInsideCard,
    isLocked: false,
    lockedMessage: null
  };
}
